import { useFlag } from '@calendly/feature-check-react';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import { useEffect, useMemo, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { isInProgress, isStartingSoon } from 'components/meetings/utils';
import { MI_MEETINGS_TOUR, StepThree, handleCheckStepEnabled } from 'features/meeting_intelligence';
import { useMeetingsContext } from 'features/meetings/contexts/MeetingsContext';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { useCurrentTime } from 'shared/contexts';
import { isCalendlyMeeting } from 'shared/utils';
import { BufferDisplay } from './buffer_display';
import { Dot, ExternalMeetingTypeDotImg, handleGetExternalDot } from './dot';
import { EditBufferPopup } from './edit_buffer_popup';
import { EventActions } from './event_actions';
import { EventDetails } from './event_details';
import { NotetakerControl } from './notetaker_control';
import { TimeDetails } from './time_details';
import { EXTERNAL_SOURCES } from '../../utils';
import { MeetingCardContainer, MeetingLink } from '../styles';
const nameAndColorIsCancelledCss = "nn4a68y";
const nameAndColorCss = "n1fbvack";
const NameAndColor = forwardRef((props, ref) => {
  const {
    isCancelled,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameAndColorCss, {
    [nameAndColorIsCancelledCss]: isCancelled
  }, props.className)} />;
});
const getBufferGroups = memberships => {
  const grouped = memberships.reduce((acc, membership) => {
    const {
      hasBeforeBuffer,
      hasAfterBuffer,
      afterBufferTime: endKey,
      beforeBufferTime: startKey
    } = membership;
    if (hasBeforeBuffer) {
      acc.startTimes[startKey] = acc.startTimes[startKey] || [];
      acc.startTimes[startKey].push(membership);
    }
    if (hasAfterBuffer) {
      acc.endTimes[endKey] = acc.endTimes[endKey] || [];
      acc.endTimes[endKey].push(membership);
    }
    return acc;
  }, {
    startTimes: {},
    endTimes: {}
  });
  return grouped;
};
const eventColumnCss = "e1vgtz2k";
const notetakerControlCss = "n1jj7kcb";
const packageNameCss = "plu6xe9";
const meetingDetailContainerSpotlightCss = "mcf9ok6";
const meetingDetailContainerIsPhoneCss = "m12l9dhx";
const meetingDetailContainerCss = "m1cmbtdf";
const MeetingDetailContainer = forwardRef((props, ref) => {
  const {
    spotlight,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(meetingDetailContainerCss, {
    [meetingDetailContainerSpotlightCss]: spotlight,
    [meetingDetailContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const innerContainerIsPhoneCss = "i1b7nrcr";
const innerContainerCss = "i1rwzvse";
const InnerContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(innerContainerCss, {
    [innerContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledMeetingLinkSpotlightCss = "s6qibw6";
const styledMeetingLinkActiveCss = "stfaxrz";
const styledMeetingLinkIsChildHoveredCss = "sz8gvr2";
const styledMeetingLinkCss = "s12u5ttt";
const StyledMeetingLink = forwardRef((props, ref) => {
  const {
    spotlight,
    active,
    isChildHovered,
    hasIndicatorBefore,
    hasIndicatorAfter,
    hasBeforeIndicator,
    ...restProps
  } = props;
  return <MeetingLink ref={ref} active={active} {...restProps} className={classNames(styledMeetingLinkCss, {
    [styledMeetingLinkSpotlightCss]: spotlight,
    [styledMeetingLinkActiveCss]: active,
    [styledMeetingLinkIsChildHoveredCss]: isChildHovered
  }, props.className)} />;
});
export const MeetingCard = ({
  event,
  otherEventsOnDay,
  selectedEventId,
  isFirst,
  isLast,
  showBuffers,
  isNext,
  hasIndicatorBefore,
  hasIndicatorAfter,
  anyInProgress,
  hasSpotlight,
  isMostRecentInProgress = false,
  trackFromContactPage = false,
  packageName = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  isFirstUpcomingForTour = false,
  ...props
}) => {
  const hasMeetingIntelligence = useFlag('meeting_intelligence');
  const {
    hasRealtimeFeatures
  } = useMeetingsContext() ?? {};
  const {
    uuid,
    eventType: {
      color
    },
    startTime,
    endTime,
    past: isPast,
    isCollective,
    isRoundRobin,
    isCollectiveRoundRobin,
    cancelled,
    source
  } = event;
  const [childButtonHovered, setChildButtonHovered] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [bufferPopupData, setBufferPopupData] = useState(null);
  const isMultiHost = isCollective || isRoundRobin || isCollectiveRoundRobin;
  const bufferGroups = useMemo(() => getBufferGroups(event.eventMemberships), [event.eventMemberships]);
  const bufferedStarts = bufferGroups.startTimes;
  const bufferedEnds = bufferGroups.endTimes;
  const isSelected = uuid === selectedEventId;
  const location = useLocation();
  const link = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.set('selected', uuid);
    params.delete('tab');
    return `${location.pathname}?${params.toString()}`;
  }, [location, uuid]);
  const showStepOne = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_ONE);
  const showStepThree = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_THREE);
  const isPhone = useDeviceType('isPhone');
  const {
    data: currentUser
  } = useGetUserQuery();
  const track = useTrack();
  const timeNotation = useSelector(timeNotationSelector);
  const timezone = useSelector(timezoneSelector);
  const currentTime = useCurrentTime();
  const eventStartTime = moment(startTime).tz(timezone);
  const eventEndTime = moment(endTime).tz(timezone);
  const inProgress = isInProgress(event, currentTime);
  const showEventActions = !isMultiHost && !cancelled && (inProgress || (hasRealtimeFeatures ? isStartingSoon(event, currentTime) : isNext && !anyInProgress));
  const setPopupData = (isBefore, userMembership) => {
    setBufferPopupData({
      isBefore,
      userMembership
    });
  };
  useEffect(() => {
    setHovered(showStepThree);
  }, [showStepThree]);
  const hasStartBuffers = showBuffers && !isEmpty(bufferedStarts);
  const hasEndBuffers = showBuffers && !isEmpty(bufferedEnds);
  const isMobileSpotlightStepThree = hasSpotlight && showStepThree && isPhone;
  const showExternalIcon = source !== EXTERNAL_SOURCES.CALENDLY;
  const handleMeetingClickTracking = () => {
    const locationAnalytics = trackFromContactPage ? 'interactions_feed_all' : 'meetings_page';
    // Analytics event for contact page doesn't have a mi_event_uuid
    const miEventUuid = trackFromContactPage ? null : event.meetingIntelligence?.uuid;
    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: 'view_meeting_details',
        location: locationAnalytics,
        event_uuid: event.uuid,
        mi_event_uuid: miEventUuid,
        is_calendly_meeting: isCalendlyMeeting(event)
      }
    });
  };
  return <>
      <MeetingCardContainer data-testid={`meeting-card-${uuid}`} isSpotlight={isMobileSpotlightStepThree} hasBeforeBuffer={hasStartBuffers} hasAfterBuffer={hasEndBuffers} isPretending={currentUser?.is_pretending} hasToggleFeature={hasMeetingIntelligence} active={isSelected}>
        {showBuffers && <BufferDisplay bufferData={bufferedStarts} currentUser={currentUser} eventStartTime={eventStartTime} eventEndTime={eventEndTime} isMultiHost={isMultiHost} timeNotation={timeNotation} timezone={timezone} setPopupData={setPopupData} isStart isFirst={isFirst} hasIndicatorBefore={hasIndicatorBefore} />}
        <StyledMeetingLink to={link} active={isSelected} hasStartBuffers={hasStartBuffers} hasEndBuffers={hasEndBuffers} isFirst={isFirst} isLast={isLast} isChildHovered={childButtonHovered || showStepOne} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} disabled={currentUser?.is_pretending} onClick={handleMeetingClickTracking} spotlight={showStepThree && hasSpotlight} hasIndicatorBefore={hasIndicatorBefore} hasIndicatorAfter={hasIndicatorAfter} {...props}>
          <InnerContainer>
            <TimeDetails ref={isFirstUpcomingForTour ? onboardingTourMeetingRefCallback : undefined} notetakerRef={isFirstUpcomingForTour ? onboardingTourNotetakerRefCallback : undefined} event={event} otherEventsOnDay={otherEventsOnDay} shouldUseContrast={isSelected || hovered} hasSpotlight={hasSpotlight} isPackageBooking={!!packageName} />
            <div className={eventColumnCss}>
              <MeetingDetailContainer spotlight={hasSpotlight && showStepThree}>
                <NameAndColor isCancelled={cancelled}>
                  {!isPhone && showExternalIcon && <ExternalMeetingTypeDotImg src={handleGetExternalDot(source)} isPast={isPast} isCancelled={cancelled} />}
                  {!isPhone && !showExternalIcon && <Dot color={color} isPast={isPast} isCancelled={cancelled} />}
                  <EventDetails event={event} hasSpotlight={hasSpotlight} />
                </NameAndColor>
                {showEventActions && <EventActions event={event} setActiveLink={link} setHovered={setChildButtonHovered} isMostRecentInProgress={isMostRecentInProgress} hasRealtimeFeatures={hasRealtimeFeatures} trackFromContactPage={trackFromContactPage} />}
              </MeetingDetailContainer>
              {packageName && <BodyText className={!isPhone ? packageNameCss : undefined} color="muted" size="small">
                {packageName}
              </BodyText>}
            </div>
            {!isPhone && <div className={notetakerControlCss}>
                <NotetakerControl ref={isFirstUpcomingForTour ? onboardingTourNotetakerRefCallback : undefined} event={event} hasSpotlight={hasSpotlight} trackFromContactPage={trackFromContactPage} />
              </div>}
          </InnerContainer>
        </StyledMeetingLink>
        {isMobileSpotlightStepThree && <StepThree isVisible={hasSpotlight} />}
        {showBuffers && <BufferDisplay bufferData={bufferedEnds} currentUser={currentUser} eventStartTime={eventStartTime} eventEndTime={eventEndTime} isMultiHost={isMultiHost} timeNotation={timeNotation} timezone={timezone} setPopupData={setPopupData} isEnd isLast={isLast} hasIndicatorAfter={hasIndicatorAfter} />}
      </MeetingCardContainer>
      {showBuffers && <EditBufferPopup eventId={event.uuid || event.id} isMultiHost={isMultiHost} eventName={event.name} isBefore={bufferPopupData?.isBefore} userMembership={bufferPopupData?.userMembership} isOpen={!isEmpty(bufferPopupData)} onClose={() => setBufferPopupData(null)} />}
    </>;
};

require("./meeting_card.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_card.js");